'use client';
import { useFormatter, useTranslations } from 'next-intl';
import { type FC, useEffect, useRef, useState } from 'react';
import { BaseLineCoupon } from '@/app/components/icons/BaseLineCoupon';
import { BaseLineInfo } from '@/app/components/icons/BaseLineInfo';
import { BaseLineLeft } from '@/app/components/icons/BaseLineLeft';
import { ChannelApple } from '@/app/components/icons/ChannelApple';
import { ChannelGoogle } from '@/app/components/icons/ChannelGoogle';
import { ChannelStripe } from '@/app/components/icons/ChannelStripe';
import { ChannelZfb } from '@/app/components/icons/ChannelZfb';
import { Credits } from '@/app/components/icons/Credits';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { UserAvatar } from '@/app/components/shared/UserAvatar';
import { Button } from '@/app/components/ui/button';
import { useFetch, useGlobalModal, useGotoManageStripe } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { SubsPlanType } from '@/app/types';
import { cn, formatExpiredDate, formatPrice, reportEvent } from '@/app/utils';
import { couponsListMyCoupons, subscriptionsGetMyExpiredSubscription } from '../apis';
import { PAY_CHANNEL_MAP, PLAN_TYPE_MAP } from '../constants';

interface HeaderProps {
  setOpenOrderList: (open: boolean) => void;
}

export const Header: FC<HeaderProps> = ({ setOpenOrderList }) => {
  const t = useTranslations();
  const format = useFormatter();

  const {
    userInfo,
    subscription,
    activeTeamBundleProduct,
    creditsInfo,
    nextPlan,
    presentPlanEffectiveTime,
    isGiftPlan,
    isTeamSpace,
    teamSpaceInfo,
    couponSummary,
    updateCouponSummary,
  } = useStore((state) => ({
    userInfo: state.userInfo,
    subscription: state.subscription,
    activeTeamBundleProduct: state.activeTeamBundleProduct,
    creditsInfo: state.creditsInfo,
    nextPlan: state.nextPlan,
    presentPlanEffectiveTime: state.presentPlanEffectiveTime,
    isGiftPlan: state.isGiftPlan,
    isTeamSpace: state.isTeamSpace,
    teamSpaceInfo: state.teamSpaceInfo,
    couponSummary: state.couponSummary,
    updateCouponSummary: state.updateCouponSummary,
  }));

  // 「我的订单」入口：个人用户可见；团队空间下仅管理员可见，普通成员隐藏
  const canViewOrder = !isTeamSpace || teamSpaceInfo?.role === 'admin';

  const { loading: manageLoading, handleGotoManageStripe } = useGotoManageStripe();
  const { open: openCouponDialog } = useGlobalModal('couponDialog');
  const { open: openCreditDetailsDialog } = useGlobalModal('creditDetailsDialog');

  const containerRef = useRef<HTMLDivElement>(null);
  const [subscriptionExpiredDiffDays, setSubscriptionExpiredDiffDays] = useState<number | null>(null);

  const plan_type = (userInfo?.subs_plan as SubsPlanType) ?? 'free';

  const sub_plan_type = subscription?.plan_type ?? 'free';
  const pay_channel = subscription?.pay_channel;
  const subscribedCredits = creditsInfo?.subscribed || 0;

  const { run: getExpiredSubscription } = useFetch(subscriptionsGetMyExpiredSubscription, {
    manual: true,
    onSuccess: ({ data }) => {
      const expiredSubscriptionTime = data?.subscription?.end_time;
      if (!expiredSubscriptionTime) return;
      const now = new Date();
      const expiredDate = new Date(expiredSubscriptionTime);
      const diffTime = Math.abs(expiredDate.getTime() - now.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      setSubscriptionExpiredDiffDays(diffDays <= 365 ? diffDays : null);
    },
  });

  const { run: runCouponList } = useFetch(couponsListMyCoupons, {
    manual: true,
    onSuccess: ({ data }) => {
      updateCouponSummary({
        unusedCount: data?.unused_count ?? 0,
        nearestUnusedExpireAt: data?.nearest_unused_expire_at ?? null,
      });
    },
  });

  useEffect(() => {
    if (plan_type === 'free' && !subscription) {
      getExpiredSubscription();
    }
  }, [plan_type, subscription, presentPlanEffectiveTime]);

  useEffect(() => {
    if (!couponSummary) {
      runCouponList({ query: { 'pager.pagesz': 1, 'pager.page': 0 } });
    }
  }, [couponSummary]);

  const formatBillingDate = (time?: string) =>
    format.dateTime(new Date(time || ''), { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone });

  const getPeriodLabel = (billing_period?: string, product_type?: string) => {
    if (billing_period === 'monthly' && product_type === 'subscription')
      return t('payments.current.subscriptionMonthly');
    if (billing_period === 'yearly' && product_type === 'subscription') return t('payments.current.subscriptionYearly');
    if (billing_period === 'monthly' && (product_type === 'pass' || product_type === 'claw_pass_bundle'))
      return t('payments.current.passMonthly');
    if (billing_period === 'yearly' && (product_type === 'pass' || product_type === 'claw_pass_bundle'))
      return t('payments.current.passYearly');
    return '';
  };

  const getPaymentTimeTitle = (): string => {
    if (!subscription) {
      return subscriptionExpiredDiffDays ? t('payments.bill.expiredDays', { days: subscriptionExpiredDiffDays }) : '';
    }
    if (subscription.status === 'canceled' && subscription.end_time) {
      return t('payments.bill.cancel', { date: formatExpiredDate(subscription.end_time, format) });
    }
    if (nextPlan?.next_plan_type) {
      return t('payments.bill.nextPlan', {
        plan: t(`payments.${nextPlan.next_plan_type}`),
        date: formatBillingDate(subscription.next_billing_time),
      });
    }
    if (subscription.product_type === 'subscription') {
      const billingDate = formatBillingDate(subscription.next_billing_time);
      const amount = formatPrice(subscription.price);
      // 试用用户特殊文案：显示首月折扣价提示
      if (subscription.is_paid_trialing) {
        return t('payments.bill.trialNextPayment', {
          date: billingDate,
          amount,
        });
      }
      return subscription.from_change_type === 'end_of_period'
        ? t('payments.bill.nextPaymentFromChange', { plan: t(`payments.${sub_plan_type}`), date: billingDate, amount })
        : t('payments.bill.nextPayment', { date: billingDate, amount });
    }
    if (subscription.product_type === 'pass') {
      return t('payments.bill.expired', { date: formatExpiredDate(subscription.period_end, format) });
    }
    return '';
  };

  if (!userInfo) return null;

  // 团队空间下用 active_team_bundle_product.tier 推导具体档位（基础版/Plus/Pro），优先于通用 plan_type 文案
  const teamTier = activeTeamBundleProduct?.tier;
  const teamTierLabel = teamTier
    ? teamTier === 'unspecified'
      ? t('payments.team_v2')
      : t(`payments.plans.tier${teamTier.charAt(0).toUpperCase()}${teamTier.slice(1)}` as 'payments.plans.tierBasic')
    : t('payments.free');
  const currentPlanLabel = isTeamSpace ? teamTierLabel : t(`payments.${plan_type}`);
  const periodLabel = isTeamSpace ? '' : getPeriodLabel(subscription?.billing_period, subscription?.product_type);
  // 付费试用中：用「N 天试用」替代常规的计费周期标签（企业空间不参与试用）
  const isPaidTrialing = !isTeamSpace && Boolean(subscription?.is_paid_trialing);
  const paidTrialDays = (() => {
    if (!isPaidTrialing || !subscription?.period_start || !subscription?.period_end) return 0;
    const start = new Date(subscription.period_start).getTime();
    const end = new Date(subscription.period_end).getTime();
    if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return 0;
    return Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)));
  })();
  const paymentTimeTitle = getPaymentTimeTitle();
  const channelLabel = pay_channel
    ? pay_channel === 'alipay'
      ? '您当时使用支付宝订阅，可以前往支付宝管理订阅'
      : t('payments.current.via', { pay_channel: PAY_CHANNEL_MAP[pay_channel] })
    : '';

  const giftCreditsExpiredLine =
    subscribedCredits > 0 && creditsInfo?.credit_sub_expires_at
      ? t('payments.current.creditsExpired', {
          credits: subscribedCredits,
          date: format.dateTime(new Date(creditsInfo.credit_sub_expires_at), {
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          }),
        })
      : '';

  const giftPlanLine = isGiftPlan
    ? presentPlanEffectiveTime.length > 0
      ? t('payments.current.presentPlanExpired', {
          dates: `${format.dateTime(new Date(presentPlanEffectiveTime[0] || ''), {
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          })} - ${format.dateTime(new Date(presentPlanEffectiveTime[1] || ''), {
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          })}`,
        })
      : t('payments.current.presentPlanExpiredForever')
    : '';

  const hasEntitlementInfo = Boolean(giftCreditsExpiredLine || giftPlanLine);
  const entitlementTooltip = hasEntitlementInfo && (
    <div className="flex flex-col gap-2 text-xs">
      {giftCreditsExpiredLine && <div>{giftCreditsExpiredLine}</div>}
      {giftPlanLine && <div>{giftPlanLine}</div>}
    </div>
  );

  const planTooltip = paymentTimeTitle && <div className="text-xs">{paymentTimeTitle}</div>;

  const handleOpenCouponDialog = () => {
    reportEvent('element_click', { from_page: 'my-subscription', element_name: 'coupons' });
    openCouponDialog();
  };

  const handleOpenCreditDetails = () => {
    reportEvent('element_click', {
      from_page: 'my-subscription',
      element_name: 'credits_button',
    });
    openCreditDetailsDialog();
  };

  const handleOpenOrderList = () => {
    setOpenOrderList(true);
    reportEvent('element_click', {
      from_page: 'my-subscription',
      element_name: 'order_button',
    });
  };

  return (
    <div
      ref={containerRef}
      className="flex min-h-10 items-center justify-between gap-4 sm:flex-col sm:items-stretch sm:gap-4"
    >
      <div className="flex min-w-0 flex-1 items-center gap-4 overflow-x-auto sm:w-full">
        <UserAvatar userInfo={userInfo} className="size-9 shrink-0" />

        <div className="flex min-w-max items-center">
          <div className="flex flex-col items-start justify-center rounded-12 px-4 py-1.5">
            <div className="flex items-center gap-1.5">
              <span className={cn('bg-clip-text font-semibold text-base', PLAN_TYPE_MAP[plan_type])}>
                {currentPlanLabel}
                {isGiftPlan ||
                  (isTeamSpace && teamTier === 'unspecified' && ` ${t('payments.current.presentPlanDesc')}`)}
              </span>
              {entitlementTooltip && (
                <CustomTooltip content={entitlementTooltip} trigger="auto" container={containerRef.current}>
                  <BaseLineInfo className="size-3.5 shrink-0 cursor-pointer text-system-white36 transition-colors hover:text-system-white" />
                </CustomTooltip>
              )}
            </div>
            <div className="whitespace-nowrap text-system-white60 text-xs">
              {t('payments.current.currentSubscription')}
            </div>
          </div>

          {!isTeamSpace && (
            <div className="flex flex-col items-start justify-center rounded-12 px-4 py-1.5">
              <div className="flex items-center gap-1.5 font-semibold">
                <span className={cn('bg-clip-text text-base', PLAN_TYPE_MAP[sub_plan_type])}>
                  {t(`payments.${sub_plan_type}`)}
                </span>
                {isPaidTrialing ? (
                  paidTrialDays > 0 && (
                    <span
                      className="whitespace-nowrap bg-clip-text font-semibold text-transparent text-xs"
                      style={{ backgroundImage: 'linear-gradient(90deg, #767BFF 0%, #F08DFF 100%)' }}
                    >
                      {t('payments.plans.trialDayLabel', { days: paidTrialDays })}
                    </span>
                  )
                ) : periodLabel ? (
                  <span className="whitespace-nowrap text-white text-xs">{periodLabel}</span>
                ) : null}
                {pay_channel === 'stripe' ? (
                  <CustomTooltip
                    content={t('payments.current.manageSubscription')}
                    trigger="auto"
                    disableHoverableContent
                    container={containerRef.current}
                  >
                    <Button
                      type="button"
                      aria-label={t('payments.current.manageSubscription')}
                      disabled={manageLoading}
                      variant="ghost"
                      className="size-5 p-0 text-system-white hover:bg-system-white08"
                      onClick={() => {
                        reportEvent('element_click', {
                          from_page: 'my-subscription',
                          element_name: 'manage_button',
                        });
                        handleGotoManageStripe();
                      }}
                    >
                      <ChannelStripe className="size-3.5" />
                    </Button>
                  </CustomTooltip>
                ) : channelLabel ? (
                  <CustomTooltip
                    content={channelLabel}
                    trigger="auto"
                    disableHoverableContent
                    container={containerRef.current}
                  >
                    <span className="inline-flex items-center">
                      {pay_channel === 'apple' && <ChannelApple className="size-3.5 cursor-pointer" />}
                      {pay_channel === 'google' && <ChannelGoogle className="size-3.5 cursor-pointer" />}
                      {pay_channel === 'alipay' && <ChannelZfb className="size-3.5 cursor-pointer" />}
                    </span>
                  </CustomTooltip>
                ) : null}
                {planTooltip && (
                  <CustomTooltip content={planTooltip} trigger="auto" container={containerRef.current}>
                    <BaseLineInfo className="size-3.5 shrink-0 cursor-pointer text-system-white36 transition-colors hover:text-system-white" />
                  </CustomTooltip>
                )}
              </div>
              <div className="whitespace-nowrap text-system-white60 text-xs">{t('payments.current.currentPlan')}</div>
            </div>
          )}

          <button
            type="button"
            aria-label={t('payments.current.currentCredits')}
            className="flex flex-col items-start justify-center rounded-12 px-4 py-1.5 transition-colors"
            onClick={handleOpenCreditDetails}
          >
            <div className="flex min-w-0 items-center justify-between gap-1.5">
              <Credits
                variant={plan_type as 'standard_v2' | 'premium_v2' | 'ultimate_v2' | 'free'}
                className="size-3 shrink-0"
              />
              <span className="font-semibold text-base text-white">{creditsInfo?.total ?? 0}</span>
              <BaseLineLeft className="size-4 shrink-0 rotate-180 text-white" />
            </div>
            <div className="whitespace-nowrap text-system-white60 text-xs">{t('payments.current.currentCredits')}</div>
          </button>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-4 text-base sm:w-full sm:gap-2">
        <Button
          type="button"
          variant="default"
          className="h-12 gap-2 rounded-16 border border-system-white08 bg-[#212428] px-6 font-semibold text-system-white hover:bg-[#2A2D32] sm:h-11 sm:flex-1 sm:px-4 sm:text-base"
          onClick={handleOpenCouponDialog}
        >
          <BaseLineCoupon className="size-6 shrink-0" />
          {t('payments.coupon.title')}
          {(couponSummary?.unusedCount ?? 0) > 0 && (
            <span className="flex min-w-6 items-center justify-center rounded-4 bg-[#5765F2] px-2 font-semibold text-sm text-white">
              {couponSummary?.unusedCount}
            </span>
          )}
        </Button>

        {canViewOrder && (
          <Button
            type="button"
            variant="default"
            className="h-12 rounded-16 border border-system-white08 bg-[#212428] px-6 font-semibold text-system-white hover:bg-[#2A2D32] sm:h-11 sm:flex-1 sm:px-4"
            onClick={handleOpenOrderList}
          >
            {t('payments.order')}
          </Button>
        )}
      </div>
    </div>
  );
};
